import { FilterCategoryEnum, FilterTypeEnum } from '@/bundles/GroupFilters/enums/FilterTypeEnum';
import {
  getSaleConditionsTextValues
} from '@/bundles/Comparables/enums/SaleConditionsEnum';
import { Nullable } from '@/utils/types';
import { AllExcludeOnlyEnum } from '@/bundles/GroupFilters/enums/AllExcludeOnlyEnum';
import { FilterInHouseButtonItems } from '@/bundles/GroupFilters/enums/InHouseFilterEnum';
import { IFilterButtonGroupItems } from '@/bundles/GroupFilters/interfaces/inputs/IFilterButtonGroupItems';
import { formatToSF } from '@/bundles/GroupFilters/helpers/formatters/formatToSF';
import {
  FilterAllExcludeFilterEnum,
  FilterInvestmentSaleButtonItems,
  FilterSubleaseButtonItems,
  FilterTransactionTypeEnum,
  FilterTransactionTypeLabels
} from '@/bundles/GroupFilters/enums/transactionType';
import i18n from '@/bundles/Localization';
import { LocalizationEnum } from '@/bundles/Localization/enum/LocalizationEnum';
import { IFilterConfigItem } from '@/bundles/GroupFilters/interfaces/IFilterConfigItem';
import { getPropertyCenterTypeTextValues } from '@/bundles/Property/enums/PropertyCenterTypeEnum';
import {
  companyPropertyRoles,
  constructionStatuses,
  FilterPrimaryUseClassItems,
  getCountriesForFilters,
  getMarketsFilters,
  getPropertyTypesWithMixedUseForFilters,
  getPropertyUsesForFilters,
  PROPERTY_TYPE_KEY,
} from '@/bundles/GroupFilters/consts';
import {
  getPropertySiteFiltersConfig
} from '@/bundles/GroupFilters/helpers/filtersConfig/reusable/getPropertySiteFiltersConfig';
import {
  getPropertyBuildingFiltersConfig
} from '@/bundles/GroupFilters/helpers/filtersConfig/reusable/getPropertyBuildingFiltersConfig';
import {
  getCraneFiltersConfigByType,
  getLoadingAndDoorsFiltersConfigByType,
  getParkingFiltersConfigByType,
  getRailFiltersConfigByType,
  getRefrigerationFiltersConfigByType,
  getUtilitiesFiltersConfigByType
} from '@/bundles/GroupFilters/helpers/filtersConfig/reusable';
import { ComparableCompanyRoles, RentScheduleTypeEnum } from '@/bundles/Comparables/types';
import { getContactRolesBySource } from '@/bundles/Contact/helpers/cards/contactsCard';
import { FilterConfidentialItems } from '@/bundles/GroupFilters/enums/FilterConfidentialEnum';
import { FilterPrimaryUseClassEnum } from '@/bundles/GroupFilters/types';
import { IGroupFilterItem } from '@/bundles/GroupFilters/helpers';
import { getShowArchiveFilterConfig } from '@/bundles/GroupFilters/helpers/filtersConfig/getShowArchiveFilterConfig';
import { getStaticSetFilterConfig } from '@/bundles/GroupFilters/helpers/filtersConfig/getStaticSetFilterConfig';

export const getComparableFilters = (filters?: Nullable<IGroupFilterItem>): IFilterConfigItem[] => {
  const propertyTypesWithMixedUse = getPropertyTypesWithMixedUseForFilters();
  const selectedPropertyTypes = filters?.[PROPERTY_TYPE_KEY]?.value || [];

  return [
    // FilterCategoryEnum.comparable_info start
    {
      title: 'In House',
      name: 'comparable_in_house',
      type: FilterTypeEnum.button_group,
      items: <IFilterButtonGroupItems>FilterInHouseButtonItems,
      defaultValue: AllExcludeOnlyEnum.all,
      category: FilterCategoryEnum.comparable_info,
    },
    {
      title: 'Confidential',
      name: 'comparable_confidential',
      type: FilterTypeEnum.button_group,
      items: <IFilterButtonGroupItems>FilterConfidentialItems,
      defaultValue: AllExcludeOnlyEnum.all,
      category: FilterCategoryEnum.comparable_info,
    },
    {
      title: 'Transaction Type',
      type: FilterTypeEnum.transaction_type_group,
      category: FilterCategoryEnum.comparable_info,
      children: [
        {
          title: 'Transaction Type',
          name: 'comparable_transaction_type',
          items: <IFilterButtonGroupItems>Object.keys(FilterTransactionTypeLabels).map((key) => ({
            text: FilterTransactionTypeLabels[key],
            value: key,
          })),
          defaultValue: FilterTransactionTypeEnum.all,
          type: FilterTypeEnum.button_group,
        },
        {
          title: 'Sublease',
          name: 'comparable_sublease',
          items: <IFilterButtonGroupItems>FilterSubleaseButtonItems,
          defaultValue: FilterAllExcludeFilterEnum.all,
          type: FilterTypeEnum.button_group,
        },
        {
          title: 'Investment Sale',
          name: 'comparable_investment',
          items: <IFilterButtonGroupItems>FilterInvestmentSaleButtonItems,
          defaultValue: FilterAllExcludeFilterEnum.all,
          type: FilterTypeEnum.button_group,
        },
      ],
    },
    {
      title: i18n.t(LocalizationEnum.sale_conditions) as string,
      items: getSaleConditionsTextValues(),
      name: 'comparable_sale_conditions',
      type: FilterTypeEnum.autocomplete_multiple_select,
      category: FilterCategoryEnum.comparable_info,
    },
    {
      title: i18n.t(LocalizationEnum.schedule_type) as string,
      items: Object.values(RentScheduleTypeEnum).map((value) => ({ text: value, value })),
      name: 'comparable_lease_type',
      type: FilterTypeEnum.autocomplete_multiple_select,
      category: FilterCategoryEnum.comparable_info,
    },
    {
      title: 'Property Type',
      type: FilterTypeEnum.property_type_group,
      category: FilterCategoryEnum.comparable_info,
      children: [
        {
          title: 'Property Type',
          name: PROPERTY_TYPE_KEY,
          type: FilterTypeEnum.autocomplete_multiple_select,
          items: propertyTypesWithMixedUse,
        },
        {
          title: 'Include Property Type As Primary Use',
          name: 'property_type_as_primary_use',
          type: FilterTypeEnum.switch,
          defaultValue: true,
        },
      ],
    },
    {
      title: 'Comparable Use Type(s)',
      name: 'comparable_use_type',
      type: FilterTypeEnum.use_type_multiple_select,
      category: FilterCategoryEnum.comparable_info,
    },
    {
      title: 'Comparable Use',
      type: FilterTypeEnum.property_use_group,
      category: FilterCategoryEnum.comparable_info,
      children: [
        {
          title: 'Comparable Use Class',
          name: 'comparable_use_class',
          type: FilterTypeEnum.button_group,
          items: FilterPrimaryUseClassItems,
          defaultValue: FilterPrimaryUseClassEnum.either,
        },
        {
          title: 'Comparable Use',
          name: 'comparable_primary_use',
          type: FilterTypeEnum.autocomplete_multiple_select,
          items: getPropertyUsesForFilters(),
        },
      ],
    },
    {
      title: 'Comp Space Size',
      name: 'comparable_size',
      type: FilterTypeEnum.number_range,
      category: FilterCategoryEnum.comparable_info,
      formatter: (v: Nullable<number>) => formatToSF(v),
    },
    {
      title: 'Land Size',
      type: FilterTypeEnum.land_size_with_pol_group,
      category: FilterCategoryEnum.comparable_info,
      children: [
        {
          title: 'Land Size',
          name: 'comparable_land_size',
          type: FilterTypeEnum.land_size_range, // sending with comparable_land_size_unit
        },
        {
          title: 'Land Size POL',
          name: 'comparable_land_pol',
          type: FilterTypeEnum.switch,
        },
      ],
    },
    {
      title: '# of Units',
      name: 'comparable_building_units',
      type: FilterTypeEnum.number_range,
      category: FilterCategoryEnum.comparable_info,
    },
    {
      title: 'Lease Rate Starting',
      name: 'comparable_lease_rate',
      type: FilterTypeEnum.currency_range,
      category: FilterCategoryEnum.comparable_info,
      additional: {
        suffix: '/ SF',
      }
    },
    {
      title: 'Sale Price',
      name: 'comparable_sales_price',
      type: FilterTypeEnum.sale_price_range, // sending with comparable_sales_price_unit
      category: FilterCategoryEnum.comparable_info,
    },
    {
      title: 'Transaction Date',
      name: 'comparable_transactionDate',
      type: FilterTypeEnum.date_range,
      category: FilterCategoryEnum.comparable_info,
    },
    {
      title: 'Commencement Date',
      name: 'comparable_commencementDate',
      type: FilterTypeEnum.date_range,
      category: FilterCategoryEnum.comparable_info,
    },
    {
      title: 'Expiration Date',
      name: 'comparable_expirationDate',
      type: FilterTypeEnum.date_range,
      category: FilterCategoryEnum.comparable_info,
    },
    {
      title: 'TI Landlord (per sf)',
      name: 'comparable_tiLandlord',
      type: FilterTypeEnum.number_range,
      category: FilterCategoryEnum.comparable_info,
    },
    {
      title: 'TI Tenant (per sf)',
      name: 'comparable_tiTenant',
      type: FilterTypeEnum.number_range,
      category: FilterCategoryEnum.comparable_info,
    },
    {
      title: 'Source Contact',
      name: 'comparable_source_contact',
      type: FilterTypeEnum.brokers_select,
      category: FilterCategoryEnum.comparable_info,
    },
    {
      title: 'Broker',
      name: 'comparable_broker',
      type: FilterTypeEnum.brokers_select,
      category: FilterCategoryEnum.comparable_info,
    },
    {
      title: 'Comparable Comments',
      name: 'comparable_comments',
      type: FilterTypeEnum.textfield,
      category: FilterCategoryEnum.comparable_info,
    },
    {
      title: i18n.t(LocalizationEnum.legacy_id) as string,
      name: 'comparable_legacy_id',
      type: FilterTypeEnum.textfield,
      category: FilterCategoryEnum.comparable_info,
    },
    // FilterCategoryEnum.comparable_info end
    // FilterCategoryEnum.property_filters start
    {
      title: 'Property Type',
      type: FilterTypeEnum.property_type_group,
      category: FilterCategoryEnum.property_filters,
      children: [
        {
          title: 'Property Type',
          name: PROPERTY_TYPE_KEY,
          type: FilterTypeEnum.autocomplete_multiple_select,
          items: propertyTypesWithMixedUse,
        },
        {
          title: 'Include Property Type As Primary Use',
          name: 'property_type_as_primary_use',
          type: FilterTypeEnum.switch,
          defaultValue: true,
        },
      ],
    },
    {
      title: 'Property Use',
      type: FilterTypeEnum.property_use_group,
      category: FilterCategoryEnum.property_filters,
      children: [
        {
          title: 'Property Use Class',
          name: 'property_use_class',
          type: FilterTypeEnum.button_group,
          items: FilterPrimaryUseClassItems,
          defaultValue: FilterPrimaryUseClassEnum.either,
        },
        {
          title: 'Property Use',
          name: 'property_primary_use',
          type: FilterTypeEnum.autocomplete_multiple_select,
          items: getPropertyUsesForFilters(),
        },
      ],
    },
    {
      title: i18n.t(LocalizationEnum.retail_center_type) as string,
      name: 'property_center_type',
      items: getPropertyCenterTypeTextValues(),
      type: FilterTypeEnum.autocomplete_multiple_select,
      category: FilterCategoryEnum.property_filters,
    },
    {
      title: 'Property Size',
      name: 'property_size',
      type: FilterTypeEnum.number_range,
      category: FilterCategoryEnum.property_filters,
      formatter: (v: Nullable<number>) => formatToSF(v),
    },
    {
      title: 'Property Land Size',
      type: FilterTypeEnum.land_size_with_pol_group,
      category: FilterCategoryEnum.property_filters,
      children: [
        {
          title: 'Property Land Size',
          name: 'property_land_size',
          type: FilterTypeEnum.land_size_range, // sending with property_land_size_unit
        },
        {
          title: 'Property Land Size POL',
          name: 'property_land_pol',
          type: FilterTypeEnum.switch,
        },
      ],
    },
    {
      title: 'Property Min Clear Height',
      name: 'property_min_clear_height',
      type: FilterTypeEnum.building_dimension_range,
      category: FilterCategoryEnum.property_filters,
    },
    {
      title: 'Property Max Clear Height',
      name: 'property_max_clear_height',
      type: FilterTypeEnum.building_dimension_range,
      category: FilterCategoryEnum.property_filters,
    },
    {
      title: 'Property # of Dock Doors',
      name: 'property_ground_door_count',
      type: FilterTypeEnum.number_range,
      category: FilterCategoryEnum.property_filters,
    },
    {
      title: 'Property # of Drive Doors',
      name: 'property_dock_door_count',
      type: FilterTypeEnum.number_range,
      category: FilterCategoryEnum.property_filters,
    },
    {
      title: 'Property Drive-In Doors Width',
      name: 'property_ground_level_width',
      type: FilterTypeEnum.building_dimension_range,
      category: FilterCategoryEnum.property_filters,
    },
    {
      title: 'Property Construction Status',
      name: 'property_construction_status',
      type: FilterTypeEnum.autocomplete_multiple_select,
      items: constructionStatuses,
      category: FilterCategoryEnum.property_filters,
    },
    // FilterCategoryEnum.property_filters end
    // FilterCategoryEnum.site start
    ...getPropertySiteFiltersConfig(),
    // FilterCategoryEnum.site end
    // FilterCategoryEnum.building start
    ...getPropertyBuildingFiltersConfig({
      propertyTypesWithMixedUse, type: 'comparable'
    }),
    // FilterCategoryEnum.building end
    // FilterCategoryEnum.crane start
    ...getCraneFiltersConfigByType(),
    // FilterCategoryEnum.crane end
    // FilterCategoryEnum.parking start
    ...getParkingFiltersConfigByType('comparable'),
    // FilterCategoryEnum.parking end
    // FilterCategoryEnum.rail start
    ...getRailFiltersConfigByType('comparable'),
    // FilterCategoryEnum.rail end
    // FilterCategoryEnum.refrigeration start
    ...getRefrigerationFiltersConfigByType('comparable'),
    // FilterCategoryEnum.refrigeration end
    // FilterCategoryEnum.doors start
    ...getLoadingAndDoorsFiltersConfigByType('comparable'),
    // FilterCategoryEnum.doors end
    // FilterCategoryEnum.utilities start
    ...getUtilitiesFiltersConfigByType('comparable'),
    // FilterCategoryEnum.utilities end
    // FilterCategoryEnum.market_location start
    {
      title: 'Address',
      name: 'property_address',
      type: FilterTypeEnum.textfield,
      category: FilterCategoryEnum.market_location,
    },
    {
      title: 'City',
      name: 'property_location_city',
      type: FilterTypeEnum.city_autocomplete,
      category: FilterCategoryEnum.market_location,
      additional: {
        citiesSource: 'property',
      }
    },
    {
      title: 'State',
      name: 'property_location_state',
      type: FilterTypeEnum.states_autocomplete,
      category: FilterCategoryEnum.market_location,
    },
    {
      title: 'Country',
      name: 'property_location_country',
      type: FilterTypeEnum.autocomplete_multiple_select,
      items: getCountriesForFilters(),
      category: FilterCategoryEnum.market_location,
    },
    {
      title: 'Zip / Postal Code',
      name: 'comparable_zip_code',
      type: FilterTypeEnum.postal_code_combobox,
      category: FilterCategoryEnum.market_location,
    },
    ...getMarketsFilters({ propertyTypes: selectedPropertyTypes }),
    // FilterCategoryEnum.market_location end
    // FilterCategoryEnum.c start
    {
      title: 'Company Name',
      name: 'company_name',
      type: FilterTypeEnum.company_name_combobox,
      category: FilterCategoryEnum.company_contact,
    },
    {
      title: 'Company Comparable Role',
      items: [
        { text: 'Seller', value: ComparableCompanyRoles.seller },
        { text: 'Buyer', value: ComparableCompanyRoles.buyer },
        { text: 'Landlord', value: ComparableCompanyRoles.landlord },
        { text: 'Tenant', value: ComparableCompanyRoles.tenant },
      ],
      name: 'company_comparable_role',
      type: FilterTypeEnum.autocomplete_multiple_select,
      category: FilterCategoryEnum.company_contact,
    },
    {
      title: 'Company Property Role',
      items: companyPropertyRoles,
      name: 'company_property_role',
      type: FilterTypeEnum.autocomplete_multiple_select,
      category: FilterCategoryEnum.company_contact,
    },
    {
      title: 'Contact Name',
      name: 'contact_names',
      type: FilterTypeEnum.contact_names_multiple_select,
      category: FilterCategoryEnum.company_contact,
    },
    {
      title: 'Contact Company Role',
      name: 'contact_company_role',
      items: getContactRolesBySource('company'),
      type: FilterTypeEnum.autocomplete_multiple_select,
      category: FilterCategoryEnum.company_contact,
    },
    {
      title: 'Contact Comparable Role',
      name: 'comparable_contact_role',
      items: [...getContactRolesBySource('comparable_sale'), ...getContactRolesBySource('comparable_lease')],
      type: FilterTypeEnum.autocomplete_multiple_select,
      category: FilterCategoryEnum.company_contact,
    },
    {
      title: 'Contact Property Role',
      items: getContactRolesBySource('property'),
      name: 'property_contact_role',
      type: FilterTypeEnum.autocomplete_multiple_select,
      category: FilterCategoryEnum.company_contact,
    },
    // FilterCategoryEnum.company_contact end
    // FilterCategoryEnum.created_edited start
    {
      title: 'Created/Edited Date',
      name: 'comparable_view_date', // NEW
      type: FilterTypeEnum.date_range,
      category: FilterCategoryEnum.created_edited,
    },
    {
      title: 'Created Date',
      name: 'comparable_created_date', // NEW
      type: FilterTypeEnum.date_range,
      category: FilterCategoryEnum.created_edited,
    },
    {
      title: 'Edited Date',
      name: 'comparable_edited_date', // NEW
      type: FilterTypeEnum.date_range,
      category: FilterCategoryEnum.created_edited,
    },
    {
      title: 'Created/Edited by',
      name: 'comparable_view', // NEW
      type: FilterTypeEnum.create_updated_by_select,
      category: FilterCategoryEnum.created_edited,
    },
    {
      title: 'Created by',
      name: 'comparable_created_by', // NEW
      type: FilterTypeEnum.create_updated_by_select,
      category: FilterCategoryEnum.created_edited,
    },
    {
      title: 'Edited by',
      name: 'comparable_edited_by', // NEW
      type: FilterTypeEnum.create_updated_by_select,
      category: FilterCategoryEnum.created_edited,
    },
    // FilterCategoryEnum.created_edited end
    getShowArchiveFilterConfig(),
    getStaticSetFilterConfig('comparable'),
  ].filter(Boolean) as IFilterConfigItem[]; // filter out null values
};
